import type { Role, User } from "@types";
import { coachCsrLanding } from "@/auth/coachLanding";
import { resolveDefaultModule } from "@/auth/defaultModule";
import { permissionsForUser } from "@/auth/permissions";
import { canAccessRoute, findMatchingRoute } from "../../router/routeConfig";
import { authPathForCompanyUrl, stripBasename } from "../hooks/companyPath";

/** Roles cleared for Operator chat — must match its guard in routeConfig. */
export const CHAT_LANDING_ROLES: ReadonlySet<Role> = new Set([
  "super",
  "owner",
  "admin",
  "member",
]);

/**
 * Resolve an application-relative redirect without trusting slash checks alone.
 * Browsers normalize backslashes as URL separators, so `/\evil.example` can
 * become a cross-origin URL even though it starts with exactly one `/`.
 */
const resolveSameOriginRedirect = (
  redirectTo: string | null,
): string | null => {
  if (
    !redirectTo?.startsWith("/") ||
    redirectTo.startsWith("//") ||
    redirectTo.includes("\\")
  ) {
    return null;
  }

  try {
    const resolved = new URL(redirectTo, window.location.origin);
    if (resolved.origin !== window.location.origin) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
};

/**
 * The role-appropriate default landing — a route the user can actually reach.
 * Exported so slug-resolution recovery (`companySlugLoader`) can send a
 * non-super/owner user somewhere valid instead of the super/owner-only
 * `/super` (owners use the separate `/company-selection` picker).
 */
export const defaultPath = (user?: User, dispatchEnabled = false): string => {
  if (user?.role === "partner") return "/partner-reporting";
  // A per-user default module wins over every role default (SD-10992). It is
  // reachability-validated inside `resolveDefaultModule`, so it can never be a
  // route the guard rejects; reps resolve to /field-sales with nothing set.
  const preferred = resolveDefaultModule(user, dispatchEnabled);
  if (preferred) return preferred;
  // coach_csr lands on its permission-derived scoped page.
  if (user?.role === "coach_csr") return coachCsrLanding(user);
  // Operator chat is role-gated. Other roles and the no-user fallthrough
  // retain the conversations landing rather than bouncing off its guard.
  if (user && CHAT_LANDING_ROLES.has(user.role)) return "/operator/chat";
  return "/conversations";
};

export const getPostLoginPath = (
  user?: User,
  dispatchEnabled = false,
): string => {
  const params = new URLSearchParams(window.location.search);
  const redirectTo = resolveSameOriginRedirect(params.get("redirectTo"));

  // `resolveSameOriginRedirect` already rejected protocol-relative and
  // cross-origin targets, so a non-null value here is application-relative.
  if (!redirectTo) {
    return defaultPath(user, dispatchEnabled);
  }

  const pathname = authPathForCompanyUrl(redirectTo.split(/[?#]/)[0]);
  if (
    !dispatchEnabled &&
    (pathname === "/dispatch" || pathname.startsWith("/dispatch/"))
  ) {
    return defaultPath(user, dispatchEnabled);
  }

  // A restricted role's `redirectTo` may point at a route its allowlist blocks
  // (e.g. authLoader stamped `?redirectTo=/conversations` before login). Honor
  // it only if the user can actually reach it — otherwise fall back to the safe
  // role default. coach_csr's allowlist is permission-derived, so
  // `canAccessRoute` MUST receive the user's permissions (same as every other
  // call site); passing role alone would fail-close a valid deep link like
  // /coach/agent-performance. `rep` (SD-10992) uses its static allowlist the
  // same way. Both fallbacks are themselves guard-derived, so this can never
  // land on a forbidden route.
  if (
    user?.role === "partner" ||
    user?.role === "coach_csr" ||
    user?.role === "rep" ||
    user?.role === "dispatcher" ||
    user?.role === "technician"
  ) {
    // Resolve a company slug (and any deploy base) before matching
    // (SD-11897): a deep link may already be slug-scoped (`/acme/coach/…`),
    // but routeConfig paths are unprefixed. The original (prefixed) `redirectTo`
    // is still what we return on success. A bare company root validates as `/`
    // (public) and is honored — navigation redirects it to the role default
    // under the slug via the router index, so restricted roles land safely.
    const route = findMatchingRoute(pathname);
    if (!route || !canAccessRoute(route, user.role, permissionsForUser(user))) {
      return defaultPath(user, dispatchEnabled);
    }
  }

  // Return an application-relative target: on a subpath deploy (`/pr-N/`) an
  // authLoader-stamped `redirectTo` can retain the deployment base, which
  // `navigate()` would then re-apply and miss the route. `stripBasename` is a
  // no-op in prod/tests where BASE_URL is `/`. Validate again after stripping:
  // `/pr-N//evil.example` is same-origin before the base is removed but would
  // otherwise become a protocol-relative redirect afterward.
  return (
    resolveSameOriginRedirect(stripBasename(redirectTo)) ??
    defaultPath(user, dispatchEnabled)
  );
};
